import { lazy, Suspense, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BrowserRouter,
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import './App.css'
import { LandingPage } from './components/shared/LandingPage'
import { B2bLandingPage } from './components/shared/B2bLandingPage'
import { PageLoadingFallback } from './components/shared/PageLoadingFallback'
import { LanguageSwitcher } from './components/shared/LanguageSwitcher'
import { BugReportButton } from './components/shared/BugReportButton'
import { ContactFormButton } from './components/shared/ContactFormButton'
import { SocialLinks } from './components/shared/SocialLinks'
import { LegalLinks } from './components/shared/LegalLinks'
import { CookieConsentProvider } from './components/shared/CookieConsentContext'
import { LegalPage } from './components/shared/LegalPage'
import { BlogListPage } from './components/shared/BlogListPage'
import { BlogPostPage } from './components/shared/BlogPostPage'
import { PvRechnerPage } from './components/shared/PvRechnerPage'
import { PvKostenRechnerPage } from './components/shared/PvKostenRechnerPage'
import { StaedteErtragRankingPage } from './components/shared/StaedteErtragRankingPage'
import { PartnerPage } from './components/shared/PartnerPage'
import { SeoLandingPage } from './components/shared/SeoLandingPage'
import { LANDING_PAGES, PROGRAMMATIC_LANDING_PAGES } from './content/landingPages'
import { IMPRESSUM_TEXT } from './content/impressum'
import { DATENSCHUTZERKLAERUNG_TEXT } from './content/datenschutzerklaerung'
import { ErrorProvider } from './error/ErrorProvider'
import { useErrorReporter } from './error/useErrorReporter'
import { ErrorPage } from './error/ErrorPage'
import { ErrorBoundary } from './error/ErrorBoundary'
import i18n, { resolveSupportedLang, SUPPORTED_LANGUAGES, type SupportedLanguage } from './i18n'
import { RouteLocaleSynchronizer } from './i18n/RouteLocaleSynchronizer'
import { isB2bHost, useSeo } from './seo/useSeo'
import { getStoredAuth } from './api/httpClient'
import { customerApi } from './api/customerApi'
import { craftsmanApi } from './api/craftsmanApi'
import { blogListPath } from './content/blog'

// Lazy-loaded: CustomerView/CraftsmanView are mutually exclusive (a visitor is only ever in one),
// and AdminConsole is reachable only via a direct, unlinked URL (see the route below) - splitting
// all three out of the main bundle cuts the ~900KB initial chunk without affecting the prerendered
// marketing pages, which don't render any of them (docs/tasks/active/seo/tasks.md #3).
const CustomerView = lazy(() =>
  import('./components/kunden/CustomerView').then((m) => ({ default: m.CustomerView })),
)
const CraftsmanView = lazy(() =>
  import('./components/handwerker/CraftsmanView').then((m) => ({ default: m.CraftsmanView })),
)
const AdminConsole = lazy(() =>
  import('./components/admin/AdminConsole').then((m) => ({ default: m.AdminConsole })),
)

function AppShell({ lang }: { lang: SupportedLanguage }) {
  const { error, clearError } = useErrorReporter()
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // Lazy initializer so entering straight at /:lang/customer from outside the app (e.g. the blog's
  // "Login" menu, see useMarketingHeaderActions) opens the right form. Only read once on mount -
  // AppShell doesn't remount for in-app navigation, which instead goes through setCustomerAuthMode
  // below (e.g. clicking "Login" on the landing page itself).
  const [customerAuthMode, setCustomerAuthMode] = useState<'login' | 'register'>(
    () => (location.state as { authMode?: 'login' | 'register' } | null)?.authMode ?? 'register',
  )
  const [craftsmanAuthMode, setCraftsmanAuthMode] = useState<'login' | 'register'>(
    () => (location.state as { authMode?: 'login' | 'register' } | null)?.authMode ?? 'register',
  )

  // Activation/password-reset links mailed out by RegistrationMailService point at
  // /:lang/customer?mode=activate&token=... (or /craftsman, or mode=reset-password) - read here
  // and threaded down instead of through location.state, since those links are followed from
  // outside the app (an email client), not from in-app navigation.
  const deepLinkMode = searchParams.get('mode')
  const deepLinkToken = searchParams.get('token') ?? undefined
  const authDeepLinkMode =
    deepLinkMode === 'activate' || deepLinkMode === 'reset-password' ? deepLinkMode : undefined

  const isRoot = location.pathname === `/${lang}`
  const isCraftsman = location.pathname.startsWith(`/${lang}/craftsman`)

  // Persistent login (task: Handwerker-Login): a stored session jumps straight past the landing
  // page into the right tab on first load, which then restores the session itself (see
  // CustomerView/CraftsmanView). Runs once per AppShell mount (empty deps) - AppShell itself never
  // remounts across client-side navigation, so clicking the logo later while still logged in shows
  // the landing page instead of bouncing straight back into the tab. Must be useEffect, not
  // useLayoutEffect: BrowserRouter subscribes to history changes in its own layout effect, and
  // layout effects fire bottom-up (children before parents) - calling navigate() from a layout
  // effect here would fire before the router's subscription exists, updating the URL without
  // triggering a re-render. useEffect fires after every layout effect in the tree has run, so the
  // router is guaranteed to be subscribed already (at the cost of one extra paint before the
  // redirect - an intentional, negligible trade-off for correctness).
  useEffect(() => {
    if (location.pathname !== `/${lang}`) return
    const stored = getStoredAuth()
    if (stored?.role === 'CUSTOMER') navigate(`/${lang}/customer`, { replace: true })
    else if (stored?.role === 'CRAFTSMAN') navigate(`/${lang}/craftsman`, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isRoot && !error) {
    const rootPageProps = {
      onSelectCustomer: () => {
        setCustomerAuthMode('register')
        navigate(`/${lang}/customer`)
      },
      onSelectCraftsman: () => {
        setCraftsmanAuthMode('register')
        navigate(`/${lang}/craftsman`)
      },
      onLoginCustomer: () => {
        setCustomerAuthMode('login')
        navigate(`/${lang}/customer`)
      },
      onLoginCraftsman: () => {
        setCraftsmanAuthMode('login')
        navigate(`/${lang}/craftsman`)
      },
    }
    // b2b.roofora.de serves the same build/container as roofora.de (see Caddyfile, nginx.conf's
    // catch-all server_name) - only the root landing page differs per hostname, everything else
    // (login/dashboard/API calls) is identical, so this single check is the entire hostname
    // switch rather than a global hostname context/provider (docs/tasks/active/features/b2b/
    // 04-seo-subdomain-deployment.md).
    return isB2bHost() ? <B2bLandingPage {...rootPageProps} /> : <LandingPage {...rootPageProps} />
  }

  return (
    <>
      <header className="app-header">
        <div className="app-header-inner">
          <button className="app-logo-link" onClick={() => navigate(`/${lang}`)}>
            Roofora
            <span className="beta-badge">Beta</span>
          </button>
          <nav className="tabs">
            <button className={!isCraftsman ? 'active' : ''} onClick={() => navigate(`/${lang}/customer`)}>
              {t('common.nav.customer')}
            </button>
            <button className={isCraftsman ? 'active' : ''} onClick={() => navigate(`/${lang}/craftsman`)}>
              {t('common.nav.craftsman')}
            </button>
          </nav>
          <div className="app-header-actions">
            <SocialLinks className="app-header-social-links" linkClassName="app-header-social-link" />
            <ContactFormButton />
            <BugReportButton />
            <LanguageSwitcher />
          </div>
        </div>
      </header>
      {/* Craftsman view gets a wider shell so the requests table can fit more columns. */}
      <div className={isCraftsman ? 'app app-wide' : 'app'}>
        <main>
          {error ? (
            <ErrorPage error={error} onReset={clearError} />
          ) : (
            <Routes>
              <Route
                path="customer/*"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <CustomerView
                      initialAuthMode={authDeepLinkMode ?? customerAuthMode}
                      initialToken={deepLinkToken}
                    />
                  </Suspense>
                }
              />
              <Route
                path="craftsman/*"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <CraftsmanView
                      initialAuthMode={authDeepLinkMode ?? craftsmanAuthMode}
                      initialToken={deepLinkToken}
                    />
                  </Suspense>
                }
              />
              <Route path="*" element={<Navigate to={`/${lang}/customer`} replace />} />
            </Routes>
          )}
        </main>
        <footer className="app-footer">
          <Link to={blogListPath(lang)} className="app-footer-blog-link">
            Blog
          </Link>
          <LegalLinks />
        </footer>
      </div>
    </>
  )
}

function RootRedirect() {
  const lang = resolveSupportedLang(i18n.resolvedLanguage)
  return <Navigate to={`/${lang}`} replace />
}

function LangLayout({ lang }: { lang: SupportedLanguage }) {
  const { i18n: i18nInstance } = useTranslation()

  useEffect(() => {
    if (i18nInstance.resolvedLanguage !== lang) {
      i18nInstance.changeLanguage(lang)
      // Persists the new language on the logged-in account (Customer/Craftsman.defaultLang) so
      // future mail (activation, offers, ...) goes out in the language the customer/craftsman is
      // actually using - see AuthService/CraftsmanAuthService and the mail package. Best-effort:
      // a failed sync (e.g. expired session) must not block switching the UI language itself.
      const stored = getStoredAuth()
      if (stored?.role === 'CUSTOMER') {
        customerApi.updateMe({ defaultLang: lang }).catch(() => {})
      } else if (stored?.role === 'CRAFTSMAN') {
        craftsmanApi.updateMe({ defaultLang: lang }).catch(() => {})
      }
    }
  }, [lang, i18nInstance])

  useSeo(lang)

  return <AppShell lang={lang} />
}

function LangRoute() {
  const { lang } = useParams<{ lang: string }>()

  if (!lang || !(SUPPORTED_LANGUAGES as readonly string[]).includes(lang)) {
    return <Navigate to="/de" replace />
  }

  return <LangLayout lang={lang as SupportedLanguage} />
}

function App() {
  return (
    <ErrorBoundary>
      <CookieConsentProvider>
        <ErrorProvider>
          <BrowserRouter>
            <RouteLocaleSynchronizer />
            <Routes>
              <Route path="/" element={<RootRedirect />} />
              {/* Not linked anywhere - reachable only via the direct URL (task: Admin-Console). */}
              <Route
                path="/admin-console/*"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <AdminConsole />
                  </Suspense>
                }
              />
              {/* German-only regardless of UI language (legal requirement) - see LegalLinks.tsx. */}
              <Route
                path="/impressum"
                element={<LegalPage title="Impressum" content={IMPRESSUM_TEXT} path="/impressum" />}
              />
              <Route
                path="/datenschutzerklaerung"
                element={
                  <LegalPage
                    title="Datenschutzerklärung"
                    content={DATENSCHUTZERKLAERUNG_TEXT}
                    path="/datenschutzerklaerung"
                  />
                }
              />
              {/* SEO blog: exists in all 3 languages, one topic per (topicId, lang) pair like the
                  landing pages below - see content/blog/types.ts. DE keeps the original bare /blog
                  URL, PL/ES use a /pl or /es prefix instead of their own index slug since the blog
                  index isn't a keyword-targeted page (see content/blog/index.ts). */}
              <Route path="/blog" element={<BlogListPage lang="de" />} />
              <Route path="/blog/:slug" element={<BlogPostPage lang="de" />} />
              <Route path="/pl/blog" element={<BlogListPage lang="pl" />} />
              <Route path="/pl/blog/:slug" element={<BlogPostPage lang="pl" />} />
              <Route path="/es/blog" element={<BlogListPage lang="es" />} />
              <Route path="/es/blog/:slug" element={<BlogPostPage lang="es" />} />
              {/* Free, anonymous PV sizing tool - SEO lead-gen funnel, German-only unlike the blog. */}
              <Route path="/pv-rechner" element={<PvRechnerPage />} />
              {/* Free, anonymous PV average-price calculator - same reasoning/pattern as
                  /pv-rechner above (tasks/new-things/preis-durchschnittsrechner/
                  05-seo-landingpages.md), bespoke route instead of a LANDING_PAGES entry: more
                  elaborate trust-building/FAQ content than a slim pSEO page needs (see
                  SeoLandingPage's calculatorPrefill slot below for the embedded-widget case,
                  added in pSEO-Story Task 02). */}
              <Route path="/pv-kostenrechner" element={<PvKostenRechnerPage />} />
              {/* SEO-4.0-Story Task 08: Ranking-Hub für die 250 Städte-Ertragsseiten unten - bespoke
                  Route aus demselben Grund wie /pv-rechner/pv-kostenrechner oben (eine 250-Zeilen-
                  Tabelle passt nicht ins generische LandingPageSection-Modell). */}
              <Route path="/photovoltaik-ertrag-staedte-ranking" element={<StaedteErtragRankingPage />} />
              {/* SEO landing pages targeting high-intent PV search terms - top-level bare-URL routes
                  like /blog and /pv-rechner above, one per (topic, language) pair, see
                  content/landingPages/types.ts. Generated from the registry rather than listed
                  individually - 21 pages (7 topics x DE/ES/PL) would make this unreadable by hand. */}
              {LANDING_PAGES.map((page) => (
                <Route key={page.slug} path={`/${page.slug}`} element={<SeoLandingPage content={page} />} />
              ))}
              {/* Programmatic (datengetriebene) pSEO landing pages - separate registry from
                  LANDING_PAGES above (see content/landingPages/index.ts), same generic
                  SeoLandingPage renderer, pre-filled calculator widget (pSEO-Story Task 02).
                  First rollout (Task 04): 7 kWp-Pilotgrößen x DE/ES/PL. */}
              {PROGRAMMATIC_LANDING_PAGES.map((page) => (
                <Route key={page.slug} path={`/${page.slug}`} element={<SeoLandingPage content={page} />} />
              ))}
              {/* Curated backlink-exchange page - German-only like the blog, see content/partners.ts. */}
              <Route path="/partner" element={<PartnerPage />} />
              <Route path="/:lang/*" element={<LangRoute />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </ErrorProvider>
      </CookieConsentProvider>
    </ErrorBoundary>
  )
}

export default App
